import { useEffect, useId, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import {
  formatCareTimeLabel,
  getCurrentCareTime,
  getDefaultCareDayOffset,
  getEffectiveCareDayOffset,
  getLateEntryDetails,
  normalizeCareTime,
  resolveNoteTiming,
} from '../utils/lateEntry';
import { useLanguage } from '../contexts/LanguageContext';

const LATE_PILL_STYLE = {
  backgroundColor: '#F3F4F6',
  color: '#6B7280',
  fontSize: '11px',
};

function LateEntryPill({ label }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 font-medium leading-none"
      style={LATE_PILL_STYLE}
    >
      {label}
    </span>
  );
}

/**
 * Read-only timing row for lists/details: care time + Late Entry pill only.
 */
export default function NoteTimingMeta({
  note = null,
  careTime: careTimeProp,
  isLateEntry: isLateEntryProp,
  className = '',
}) {
  const { t, language } = useLanguage();
  const timing = note ? resolveNoteTiming(note) : null;

  const careTime =
    careTimeProp !== undefined
      ? careTimeProp
      : timing?.careTime || null;

  const careDayOffset =
    timing?.careDayOffset === 0 || timing?.careDayOffset === 1
      ? timing.careDayOffset
      : getEffectiveCareDayOffset(
          careTime,
          timing?.documentationTime || new Date().toISOString(),
          null
        );

  const isLateEntry =
    typeof isLateEntryProp === 'boolean'
      ? isLateEntryProp
      : Boolean(timing?.isLateEntry);

  return (
    <p
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-900 ${className}`.trim()}
    >
      <span>
        {t('timeLabel')}: {formatCareTimeLabel(careTime, careDayOffset, language)}
      </span>
      {isLateEntry && <LateEntryPill label={t('lateEntry')} />}
    </p>
  );
}

/**
 * Editable care-time control for the note review screen.
 * Recalculates late-entry status against documentation_time on Confirm.
 */
export function CareTimeEditor({
  careTime,
  careDayOffset = null,
  documentationTime,
  onChange,
  className = '',
}) {
  const { t, language } = useLanguage();
  const inputId = useId();
  const timeInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draftTime, setDraftTime] = useState(() => normalizeCareTime(careTime) || getCurrentCareTime());
  const [draftOffset, setDraftOffset] = useState(0);

  const resolvedTime = normalizeCareTime(careTime) || getCurrentCareTime();
  const displayOffset =
    careDayOffset === 0 || careDayOffset === 1
      ? careDayOffset
      : getEffectiveCareDayOffset(resolvedTime, documentationTime, careDayOffset);
  const details = getLateEntryDetails(resolvedTime, documentationTime, careDayOffset);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => timeInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const openPicker = () => {
    setDraftTime(resolvedTime);
    setDraftOffset(
      careDayOffset === 0 || careDayOffset === 1
        ? careDayOffset
        : getDefaultCareDayOffset()
    );
    setOpen(true);
  };

  const confirm = () => {
    const normalized = normalizeCareTime(draftTime) || getCurrentCareTime();
    const nextDetails = getLateEntryDetails(normalized, documentationTime, draftOffset);
    onChange?.({
      care_time: normalized,
      care_day_offset: draftOffset,
      is_late_entry: nextDetails.isLateEntry,
      late_entry_diff_minutes: nextDetails.diffMinutes,
    });
    setOpen(false);
  };

  return (
    <div className={`rounded-xl border border-gray-100 bg-gray-50 px-1 py-2 ${className}`.trim()}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openPicker())}
          className="inline-flex min-w-0 items-center gap-1.5 text-left text-sm font-medium text-gray-900"
          aria-expanded={open}
          aria-controls={inputId}
        >
          <Clock
            className="h-3.5 w-3.5 shrink-0 text-gray-700"
            strokeWidth={2}
            aria-hidden="true"
          />
          <span>
            {t('timeLabel')}: {formatCareTimeLabel(resolvedTime, displayOffset, language)}
          </span>
        </button>
        {details.isLateEntry && <LateEntryPill label={t('lateEntry')} />}
      </div>

      {open && (
        <div className="mt-2 space-y-2.5 border-t border-gray-200 pt-2">
          <div className="inline-flex gap-1.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setDraftOffset(0)}
              className={`rounded-full px-3 py-1 ${
                draftOffset === 0
                  ? 'bg-primary text-white'
                  : 'border border-gray-300 bg-white text-gray-600'
              }`}
            >
              {t('today')}
            </button>
            <button
              type="button"
              onClick={() => setDraftOffset(1)}
              className={`rounded-full px-3 py-1 ${
                draftOffset === 1
                  ? 'bg-primary text-white'
                  : 'border border-gray-300 bg-white text-gray-600'
              }`}
            >
              {t('yesterday')}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={inputId} className="sr-only">
              {t('timeOfCare')}
            </label>
            <input
              id={inputId}
              ref={timeInputRef}
              type="time"
              step="60"
              value={draftTime}
              onChange={(event) => setDraftTime(event.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800"
            />
            <button
              type="button"
              onClick={confirm}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white"
            >
              {t('confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { LateEntryPill };
